import React, { CSSProperties, useState } from 'react';
import { styled } from '../../../../styles';
import { SingleSelectProps, OptionTypeWithCode } from '../../types';
import { Tooltip, shouldEnableTooltip } from '../../../Tooltip';
import { Skeleton } from '../../../Skeleton';
import { SelectIconWrapper } from './SelectIconWrapper';
import { SecondaryLabels } from './SecondaryLabelItem';
import { Bullet } from './Bullet';

interface SelectedOptionProps {
  selectedOption: OptionTypeWithCode;
  visibleElementsWhenSelected: SingleSelectProps<any>['visibleElementsWhenSelected'];
  onClick?(e: React.MouseEvent<HTMLDivElement>): void;
  variant?: 'chip' | 'small' | 'medium';
  style?: CSSProperties;
  dataTest?: string;
  noWrapSubLabel?: boolean;
  loading?: boolean;
}

export const RichTextPresenter: React.FC<React.PropsWithChildren<SelectedOptionProps>> = ({
  selectedOption,
  visibleElementsWhenSelected,
  onClick,
  variant = 'medium',
  style,
  dataTest,
  noWrapSubLabel,
  loading
}) => {
  const label = visibleElementsWhenSelected?.label ? selectedOption.label : null;
  const icon = visibleElementsWhenSelected?.icon && selectedOption.icon ? selectedOption.icon : null;
  const [showTooltip, setTooltipState] = useState(false);
  const textVariant = variant === 'chip' ? 'small' : variant;
  return (
    <StyledWrapper onClick={onClick} style={style} data-test={dataTest ?? 'rich-text-presenter'} title="">
      {!!icon && (
        <SelectIconWrapper data-test="icon" variant={variant}>
          {icon}
        </SelectIconWrapper>
      )}
      {!!label && (
        <Tooltip title={label} placement="top" disabled={!showTooltip}>
          <StyledLabel
            className="label-item text"
            $variant={textVariant}
            onMouseEnter={(e) => {
              setTooltipState((prevState) => {
                if (prevState) {
                  return true;
                }
                return shouldEnableTooltip(e.target as HTMLSpanElement);
              });
            }}
          >
            {!loading || (loading && !!selectedOption.value && selectedOption.value !== selectedOption.label) ? (
              label
            ) : (
              <Skeleton />
            )}
          </StyledLabel>
        </Tooltip>
      )}
      <SecondaryLabels
        noWrapSubLabel={!!noWrapSubLabel}
        startAdornment={(elements) => {
          if (!elements.length) {
            return null;
          }
          if (elements[0]?.type === 'sub-label') {
            return <Bullet />;
          }
          return <StyledSpacer />;
        }}
        option={selectedOption}
        visible={visibleElementsWhenSelected}
        variant={textVariant}
        codeInBrackets
      />
    </StyledWrapper>
  );
};

const StyledSpacer = styled('div')`
  width: ${({ theme }) => theme.spacingSize.m};
  height: ${({ theme }) => theme.spacingSize.m};
`;
const StyledWrapper = styled('div')`
  display: inline-flex;
  align-items: center;
  align-self: auto;
  max-width: 100%;

  & > * {
    display: inline-block;
    align-self: center;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  & > .MuiListItemAvatar-root {
      top: 1px;
      position: relative;
  }

    && {
    @media (max-width: 640px) {
      //display: block;

      .small,
      .medium {
        display: none;
      }
    }
  }
`;

const StyledLabel = styled('span')<{ $variant: 'small' | 'medium' }>`
  & + .label-item {
    margin-left: ${({ theme }) => theme.spacingSize.m};
  }
`;
